import { Box, Grid, TextField, Typography, createTheme } from '@mui/material';
import {
  isNewFttsImplementation,
  getFirstOccurredTimestamp,
  getLastOccurredTimestamp,
} from '../../common/functions/ftts_new_implementation.function';
import {
  isNewIpranImplementation,
  getFirstOccurredTimestamp as getFirstOccurredTimestampIpran,
  getLastOccurredTimestamp as getLastOccurredTimestampIpran,
} from '../../common/functions/ipran_new_implementation.function';
import {
  isNewOtnImplementation,
  isOtnEndpointAlarm,
  getFirstOccurredTimestamp as getFirstOccurredTimestampOtn,
  getLastOccurredTimestamp as getLastOccurredTimestampOtn,
} from '../../common/functions/otn_new_implementation.function';
import { OtnEndpointsExpandedDetails } from '../ActiveAlarmsCard/otnEndpointUi';
import '../ActiveAlarmsCard/otnEndpointStyles.css';

// Utility function to calculate and format duration
const calculateDuration = (alarmActiveTime, alarmInactiveTime) => {
  if (!alarmActiveTime || !alarmInactiveTime) {
    return 'N/A';
  }

  try {
    const activeTime = new Date(alarmActiveTime);
    const inactiveTime = new Date(alarmInactiveTime);

    if (isNaN(activeTime.getTime()) || isNaN(inactiveTime.getTime())) {
      return 'N/A';
    }

    const diffMs = inactiveTime.getTime() - activeTime.getTime();

    if (diffMs < 0) {
      return 'N/A';
    }

    const totalSeconds = Math.floor(diffMs / 1000);
    const totalMinutes = Math.floor(totalSeconds / 60);
    const totalHours = Math.floor(totalMinutes / 60);
    const totalDays = Math.floor(totalHours / 24);

    const days = totalDays;
    const hours = totalHours % 24;
    const minutes = totalMinutes % 60;

    const parts = [];

    if (days > 0) {
      parts.push(`${days} day${days > 1 ? 's' : ''}`);
    }
    if (hours > 0) {
      parts.push(`${hours} hour${hours > 1 ? 's' : ''}`);
    }
    if (minutes > 0) {
      parts.push(`${minutes} min${minutes > 1 ? 's' : ''}`);
    }

    if (parts.length === 0) {
      return '0 mins';
    }

    return parts.join(' ');
  } catch (error) {
    return 'N/A';
  }
};

// Utility function to calculate alarm active time for live alarms
const calculateAlarmActiveTime = (alarmActiveTime) => {
  if (!alarmActiveTime) {
    return 'N/A';
  }

  try {
    const startTime = new Date(alarmActiveTime);
    const currentTime = new Date();

    if (isNaN(startTime.getTime())) {
      return 'N/A';
    }

    const diffMs = currentTime.getTime() - startTime.getTime();

    if (diffMs < 0) {
      return 'N/A';
    }

    const totalSeconds = Math.floor(diffMs / 1000);
    const totalMinutes = Math.floor(totalSeconds / 60);
    const totalHours = Math.floor(totalMinutes / 60);
    const totalDays = Math.floor(totalHours / 24);

    const days = totalDays;
    const hours = totalHours % 24;
    const minutes = totalMinutes % 60;

    const parts = [];

    if (days > 0) {
      parts.push(`${days} day${days > 1 ? 's' : ''}`);
    }
    if (hours > 0) {
      parts.push(`${hours} hour${hours > 1 ? 's' : ''}`);
    }
    if (minutes > 0) {
      parts.push(`${minutes} min${minutes > 1 ? 's' : ''}`);
    }

    if (parts.length === 0) {
      return '0 mins';
    }

    return parts.join(' ');
  } catch (error) {
    return 'N/A';
  }
};

export const ExpandableTable = ({ row, alarmType }) => {
  const {
    lat,
    long,
    alarm_source,
    xmin,
    ymin,
    first_occurred,
    last_occurred,
    alarm_active_ts,
    alarm_inactive_ts,
    xmax,
    ymax,
  } = row?.original;

  const isOtnEndpointMode =
    sessionStorage.getItem('selectedTab') === 'otn' &&
    isNewOtnImplementation() &&
    isOtnEndpointAlarm(row?.original);

  if (isOtnEndpointMode) {
    return (
      <Box style={{ background: '#FCFCFC', padding: '12px 16px 16px' }}>
        <OtnEndpointsExpandedDetails alarm={row?.original} alarmType={alarmType} />
        <Grid container spacing={4}>
          <Grid item xs={0.5}></Grid>
          <Grid item xs={2}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Typography
                style={{
                  color: '#516176',
                  fontSize: '12px',
                  fontWeight: '600',
                  lineHeight: '18px',
                }}
              >
                Latitude:
              </Typography>
              <span
                style={{
                  color: '#667085',
                  fontSize: '12px',
                  fontWeight: '400',
                  lineHeight: '18px',
                  fontFamily: 'Poppins',
                }}
              >
                {lat || 'N/A'}
              </span>
            </div>
          </Grid>
          <Grid item xs={2}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Typography
                style={{
                  color: '#516176',
                  fontSize: '12px',
                  fontWeight: '600',
                  lineHeight: '18px',
                }}
              >
                Longitude:
              </Typography>
              <span
                style={{
                  color: '#667085',
                  fontSize: '12px',
                  fontWeight: '400',
                  lineHeight: '18px',
                  fontFamily: 'Poppins',
                }}
              >
                {long || 'N/A'}
              </span>
            </div>
          </Grid>
          <Grid item xs={2}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Typography
                style={{
                  color: '#516176',
                  fontSize: '12px',
                  fontWeight: '600',
                  lineHeight: '18px',
                }}
              >
                Length:
              </Typography>
              <span
                style={{
                  color: '#667085',
                  fontSize: '12px',
                  fontWeight: '400',
                  lineHeight: '18px',
                  fontFamily: 'Poppins',
                }}
              >
                {row?.original?.length ? `${parseFloat(row.original.length).toFixed(2)} km` : 'N/A'}
              </span>
            </div>
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box style={{ background: '#FCFCFC' }}>
      <Grid container spacing={4} mb={3}>
        <Grid item xs={0.5}></Grid>
        <Grid item xs={2}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography
              style={{ color: '#516176', fontSize: '12px', fontWeight: '600', lineHeight: '18px' }}
            >
              X-min:
            </Typography>
            <span
              style={{
                color: '#667085',
                fontSize: '12px',
                fontWeight: '400',
                lineHeight: '18px',
                fontFamily: 'Poppins',
              }}
            >
              {xmin || 'N/A'}
            </span>
          </div>
        </Grid>
        <Grid item xs={2}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography
              style={{ color: '#516176', fontSize: '12px', fontWeight: '600', lineHeight: '18px' }}
            >
              Y-min:
            </Typography>
            <span
              style={{
                color: '#667085',
                fontSize: '12px',
                fontWeight: '400',
                lineHeight: '18px',
                fontFamily: 'Poppins',
              }}
            >
              {ymin || 'N/A'}
            </span>
          </div>
        </Grid>
        <Grid item xs={3}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography
              style={{ color: '#516176', fontSize: '12px', fontWeight: '600', lineHeight: '18px' }}
            >
              {(sessionStorage.getItem('selectedTab') === 'ftts' && isNewFttsImplementation()) ||
              (sessionStorage.getItem('selectedTab') === 'ipran' && isNewIpranImplementation()) ||
              (sessionStorage.getItem('selectedTab') === 'otn' && isNewOtnImplementation())
                ? 'Alarm Active Time:'
                : 'First Occurred:'}
            </Typography>
            <span
              style={{
                color: '#667085',
                fontSize: '12px',
                fontWeight: '400',
                lineHeight: '18px',
                fontFamily: 'Poppins',
              }}
            >
              {sessionStorage.getItem('selectedTab') === 'ipran'
                ? getFirstOccurredTimestampIpran(row?.original) || 'N/A'
                : sessionStorage.getItem('selectedTab') === 'otn'
                  ? getFirstOccurredTimestampOtn(row?.original) || 'N/A'
                  : getFirstOccurredTimestamp(row?.original) || 'N/A'}
            </span>
          </div>
        </Grid>
        <Grid item xs={1.5}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography
              style={{ color: '#516176', fontSize: '12px', fontWeight: '600', lineHeight: '18px' }}
            >
              Latitude:
            </Typography>
            <span
              style={{
                color: '#667085',
                fontSize: '12px',
                fontWeight: '400',
                lineHeight: '18px',
                fontFamily: 'Poppins',
              }}
            >
              {lat || 'N/A'}
            </span>
          </div>
        </Grid>
        <Grid item xs={3}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography
              style={{ color: '#516176', fontSize: '12px', fontWeight: '600', lineHeight: '18px' }}
            >
              Alarm Source:
            </Typography>
            <span
              style={{
                color: '#667085',
                fontSize: '12px',
                fontWeight: '400',
                lineHeight: '18px',
                fontFamily: 'Poppins',
              }}
            >
              {alarm_source || 'N/A'}
            </span>
          </div>
        </Grid>
      </Grid>
      <Grid container spacing={4}>
        <Grid item xs={0.5}></Grid>
        <Grid item xs={2}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography
              style={{ color: '#516176', fontSize: '12px', fontWeight: '600', lineHeight: '18px' }}
            >
              X-max:
            </Typography>
            <span
              style={{
                color: '#667085',
                fontSize: '12px',
                fontWeight: '400',
                lineHeight: '18px',
                fontFamily: 'Poppins',
              }}
            >
              {xmax || 'N/A'}
            </span>
          </div>
        </Grid>
        <Grid item xs={2}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography
              style={{ color: '#516176', fontSize: '12px', fontWeight: '600', lineHeight: '18px' }}
            >
              Y-max:
            </Typography>
            <span
              style={{
                color: '#667085',
                fontSize: '12px',
                fontWeight: '400',
                lineHeight: '18px',
                fontFamily: 'Poppins',
              }}
            >
              {ymax || 'N/A'}
            </span>
          </div>
        </Grid>
        <Grid item xs={3}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography
              style={{ color: '#516176', fontSize: '12px', fontWeight: '600', lineHeight: '18px' }}
            >
              {(sessionStorage.getItem('selectedTab') === 'ftts' && isNewFttsImplementation()) ||
              (sessionStorage.getItem('selectedTab') === 'ipran' && isNewIpranImplementation()) ||
              (sessionStorage.getItem('selectedTab') === 'otn' && isNewOtnImplementation())
                ? 'Alarm InActive Time:'
                : 'Last Occurred:'}
            </Typography>
            <span
              style={{
                color: '#667085',
                fontSize: '12px',
                fontWeight: '400',
                lineHeight: '18px',
                fontFamily: 'Poppins',
              }}
            >
              {sessionStorage.getItem('selectedTab') === 'ipran'
                ? getLastOccurredTimestampIpran(row?.original) || 'N/A'
                : sessionStorage.getItem('selectedTab') === 'otn'
                  ? getLastOccurredTimestampOtn(row?.original) || 'N/A'
                  : getLastOccurredTimestamp(row?.original) || 'N/A'}
            </span>
          </div>
        </Grid>
        <Grid item xs={1.5}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography
              style={{ color: '#516176', fontSize: '12px', fontWeight: '600', lineHeight: '18px' }}
            >
              Longitude:
            </Typography>
            <span
              style={{
                color: '#667085',
                fontSize: '12px',
                fontWeight: '400',
                lineHeight: '18px',
                fontFamily: 'Poppins',
              }}
            >
              {long || 'N/A'}
            </span>
          </div>
        </Grid>
        {/* Duration field - only show for FTTS/IPRAN with new implementation and history alarms */}
        {((sessionStorage.getItem('selectedTab') === 'ftts' && isNewFttsImplementation()) ||
          (sessionStorage.getItem('selectedTab') === 'ipran' && isNewIpranImplementation()) ||
          (sessionStorage.getItem('selectedTab') === 'otn' && isNewOtnImplementation())) &&
          alarmType === 'history' && (
            <Grid item xs={3}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Typography
                  style={{
                    color: '#516176',
                    fontSize: '12px',
                    fontWeight: '600',
                    lineHeight: '18px',
                  }}
                >
                  Alarm Duration:
                </Typography>
                <span
                  style={{
                    color: '#667085',
                    fontSize: '12px',
                    fontWeight: '400',
                    lineHeight: '18px',
                    fontFamily: 'Poppins',
                  }}
                >
                  {calculateDuration(
                    sessionStorage.getItem('selectedTab') === 'ipran'
                      ? getFirstOccurredTimestampIpran({ alarm_active_ts, first_occurred })
                      : sessionStorage.getItem('selectedTab') === 'otn'
                        ? getFirstOccurredTimestampOtn({ alarm_active_ts, first_occurred })
                        : getFirstOccurredTimestamp({ alarm_active_ts, first_occurred }),
                    sessionStorage.getItem('selectedTab') === 'ipran'
                      ? getLastOccurredTimestampIpran({ alarm_inactive_ts, last_occurred })
                      : sessionStorage.getItem('selectedTab') === 'otn'
                        ? getLastOccurredTimestampOtn({ alarm_inactive_ts, last_occurred })
                        : getLastOccurredTimestamp({ alarm_inactive_ts, last_occurred })
                  )}
                </span>
              </div>
            </Grid>
          )}
        {/* Alarm Active Time field - only show for FTTS/IPRAN with new implementation and live alarms */}
        {((sessionStorage.getItem('selectedTab') === 'ftts' && isNewFttsImplementation()) ||
          (sessionStorage.getItem('selectedTab') === 'ipran' && isNewIpranImplementation()) ||
          (sessionStorage.getItem('selectedTab') === 'otn' && isNewOtnImplementation())) &&
          alarmType === 'live' && (
            <Grid item xs={3}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Typography
                  style={{
                    color: '#516176',
                    fontSize: '12px',
                    fontWeight: '600',
                    lineHeight: '18px',
                  }}
                >
                  Alarm Lifetime:
                </Typography>
                <span
                  style={{
                    color: '#667085',
                    fontSize: '12px',
                    fontWeight: '400',
                    lineHeight: '18px',
                    fontFamily: 'Poppins',
                  }}
                >
                  {calculateAlarmActiveTime(
                    sessionStorage.getItem('selectedTab') === 'ipran'
                      ? getFirstOccurredTimestampIpran({ alarm_active_ts, first_occurred })
                      : sessionStorage.getItem('selectedTab') === 'otn'
                        ? getFirstOccurredTimestampOtn({ alarm_active_ts, first_occurred })
                        : getFirstOccurredTimestamp({ alarm_active_ts, first_occurred })
                  )}
                </span>
              </div>
            </Grid>
          )}
      </Grid>
    </Box>
  );
};
